/**
 * Leredd Device Finder — weighted scoring quiz
 * No PII, no external requests. Pure JS scoring against static catalog map.
 * Entry: data-leredd-device-finder attribute on any container element.
 */

const LEREDD_CATALOG = [
  { handle: "super-b-full-body", name: "SUPER B Full Body",   tagline: "Maximum full-body coverage — the premium daily therapy solution",    goals: ["joints","recovery","fullbody","clinic"], space: "full",    budgetTier: 3 },
  { handle: "nirflex",           name: "NIRFLEX",             tagline: "Flexible full-body wrap — versatile targeted near-infrared therapy",   goals: ["joints","recovery","skin","fullbody"],   space: "full",    budgetTier: 3 },
  { handle: "dome-720",          name: "DOME 720",            tagline: "Hands-free dome design for immersive full-body sessions",             goals: ["fullbody","recovery","joints","clinic"],  space: "full",    budgetTier: 3 },
  { handle: "biohax-m3-648",     name: "BIOHAX M3 648",      tagline: "Large desktop panel — serious coverage for the home clinic",           goals: ["joints","recovery","fullbody","clinic"], space: "desktop", budgetTier: 2 },
  { handle: "biohax-m3-432",     name: "BIOHAX M3 432",      tagline: "Mid-size desktop panel — targeted treatment with clinical power",      goals: ["skin","joints","recovery","clinic"],     space: "desktop", budgetTier: 2 },
  { handle: "biocell-60",        name: "BIOCELL 60",          tagline: "Compact desktop device — face and upper body therapy",                goals: ["skin","recovery"],                        space: "desktop", budgetTier: 1 },
  { handle: "m4-40",             name: "M4 40",               tagline: "Portable panel — ideal for spot treatment and travel use",            goals: ["skin","joints","portable"],               space: "compact", budgetTier: 1 },
  { handle: "miniquad-15",       name: "MINIQUAD 15",         tagline: "Entry-level handheld — targeted skin and spot work",                  goals: ["skin","portable"],                        space: "compact", budgetTier: 1 },
  { handle: "hf-gloves",         name: "H&F Gloves",          tagline: "Wearable gloves — arthritis, joint pain and nerve support",           goals: ["joints","portable"],                      space: "compact", budgetTier: 1 },
  { handle: "orisled",           name: "ORISLED",             tagline: "Oral LED device — gum health and facial circulation",                 goals: ["skin","portable"],                        space: "compact", budgetTier: 1 }
];

const BUDGET_MAX_TIER = { budget: 1, mid: 2, premium: 3 };

function scoreDevice(device, answers) {
  let score = 0;
  (answers.goals || []).forEach(g => { if (device.goals.includes(g)) score += 3; });
  if (answers.space && device.space === answers.space) score += 2;
  if (answers.budget && device.budgetTier <= (BUDGET_MAX_TIER[answers.budget] || 3)) score += 1;
  return score;
}

function getRecommendations(answers) {
  return [...LEREDD_CATALOG]
    .map(d => ({ ...d, score: scoreDevice(d, answers) }))
    .sort((a, b) => b.score - a.score)
    .slice(0, 3);
}

class LereddDeviceFinder {
  constructor(el) {
    this.el = el;
    this.answers = { goals: [] };
    this.step = 0;
    this.steps = ["goals", "space", "budget", "results"];
    this.render();
  }

  render() {
    this.el.innerHTML = this["build_" + this.steps[this.step]]();
    this.bind();
  }

  build_goals() {
    return `<div class="ldf__step">
      <p class="ldf__step-num">Step 1 of 3</p>
      <h2 class="ldf__question">What are your main goals?</h2>
      <p class="ldf__hint">Select all that apply</p>
      <div class="ldf__options ldf__options--multi">
        ${[["skin","For Skin — face masks, anti-ageing, nasal therapy"],["joints","For Pain & Joints — wraps, pads, gloves, arthritis"],["recovery","For Recovery — panels, blankets, full-body sessions"],["fullbody","For Full Body — large panels, 360 beds, blankets"],["portable","Portable — compact, travel-ready, wearable devices"],["clinic","Clinic & Professional — commercial-grade devices"]].map(([v,l])=>`<button class="ldf__option" data-val="${v}" aria-pressed="false">${l}</button>`).join("")}
      </div>
      <button class="ldf__next ldf__btn-primary" disabled>Next →</button>
    </div>`;
  }

  build_space() {
    return `<div class="ldf__step">
      <p class="ldf__step-num">Step 2 of 3</p>
      <h2 class="ldf__question">How much space do you have?</h2>
      <div class="ldf__options">
        <button class="ldf__option" data-val="full">Full-body — room for a large panel, dome, or wrap</button>
        <button class="ldf__option" data-val="desktop">Desktop / mid-size — a panel on a desk or stand</button>
        <button class="ldf__option" data-val="compact">Compact & portable — handheld or travel-friendly</button>
      </div>
      <button class="ldf__back ldf__btn-secondary">← Back</button>
    </div>`;
  }

  build_budget() {
    return `<div class="ldf__step">
      <p class="ldf__step-num">Step 3 of 3</p>
      <h2 class="ldf__question">What's your approximate budget?</h2>
      <div class="ldf__options">
        <button class="ldf__option" data-val="budget">Under $500 — getting started</button>
        <button class="ldf__option" data-val="mid">$500–$1,500 — serious home user</button>
        <button class="ldf__option" data-val="premium">$1,500+ — clinical-grade investment</button>
      </div>
      <button class="ldf__back ldf__btn-secondary">← Back</button>
    </div>`;
  }

  build_results() {
    const recs = getRecommendations(this.answers);
    return `<div class="ldf__step">
      <h2 class="ldf__question">Your top matches</h2>
      <p class="ldf__hint">Based on your goals, here are the best Leredd devices for you.</p>
      <div class="ldf__results">
        ${recs.map((d, i) => `
          <div class="ldf__result-card${i===0?" ldf__result-card--primary":""}">
            ${i===0?'<span class="ldf__badge">Best match</span>':""}
            <h3 class="ldf__result-name">${d.name}</h3>
            <p class="ldf__result-tagline">${d.tagline}</p>
            <a href="/products/${d.handle}" class="ldf__btn-primary">Shop Now</a>
          </div>`).join("")}
      </div>
      <button class="ldf__restart ldf__btn-secondary">Start over</button>
    </div>`;
  }

  bind() {
    this.el.querySelectorAll(".ldf__options--multi .ldf__option").forEach(btn => {
      btn.addEventListener("click", () => {
        const v = btn.dataset.val, pressed = btn.getAttribute("aria-pressed") === "true";
        btn.setAttribute("aria-pressed", String(!pressed));
        btn.classList.toggle("ldf__option--selected", !pressed);
        this.answers.goals = pressed ? this.answers.goals.filter(g=>g!==v) : [...this.answers.goals, v];
        const next = this.el.querySelector(".ldf__next");
        if (next) next.disabled = this.answers.goals.length === 0;
      });
    });

    this.el.querySelectorAll(".ldf__options:not(.ldf__options--multi) .ldf__option").forEach(btn => {
      btn.addEventListener("click", () => {
        this.answers[this.steps[this.step]] = btn.dataset.val;
        this.step++;
        this.render();
      });
    });

    const next = this.el.querySelector(".ldf__next");
    if (next) next.addEventListener("click", () => { this.step++; this.render(); });

    const back = this.el.querySelector(".ldf__back");
    if (back) back.addEventListener("click", () => { this.step--; this.render(); });

    const restart = this.el.querySelector(".ldf__restart");
    if (restart) restart.addEventListener("click", () => { this.answers={goals:[]}; this.step=0; this.render(); });
  }
}

document.addEventListener("DOMContentLoaded", () => {
  document.querySelectorAll("[data-leredd-device-finder]").forEach(el => new LereddDeviceFinder(el));
});
